import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDSwitch from "components/MDSwitch";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from 'yup';

const ForumCreateEdit = ({ id, closeCreateEdit }) => {

    const {
        nome,
        telefone,
        pais,
        estado,
        cidade,
        logradouro,
        bairro,
        cep,
        numero,
        complemento } = configForm.formField

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [cidadeList, setCidadeList] = useState([]);
    const [estadoList, setEstadoList] = useState([]);

    useEffect(() => {
        get(`/addresses/states`)
            .then(({ list }) => {
                setEstadoList(list);
            }).catch(() => {
                setEstadoList([]);
            });
    }, []);

    function selectEstadoCidade(estado) {
        get(`/addresses/cities?stateId=${estado.id}`)
            .then(({ list }) => {
                setCidadeList(list);
            }).catch(() => {
                setCidadeList([]);
            });
    }

    const buscarCep = async (cepStr, setFieldValue) => {
        const cepFormatado = cepStr ? cepStr.replace(/\D/g, '') : '';
        if (cepFormatado.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cepFormatado}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setFieldValue('logradouro', data.logradouro);
                    setFieldValue('bairro', data.bairro);
                    if (data.complemento) setFieldValue('complemento', data.complemento);

                    const foundState = estadoList.find(e => e.codEstado === data.uf || e.sigla === data.uf || e.descricao === data.uf || e.nome === data.uf);
                    if (foundState) {
                        setFieldValue('estado', foundState);
                        get(`/addresses/cities?stateId=${foundState.id}`)
                            .then(({ list }) => {
                                setCidadeList(list);
                                const foundCity = list.find(c => c.descricao.toUpperCase() === data.localidade.toUpperCase() || c.codMunicipio == data.ibge);
                                if (foundCity) {
                                    setFieldValue('cidade', foundCity);
                                }
                            });
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        }
    }

    const handleSubmit = (values, actions) => {

        const body = {
            id: values.id,
            nome: values.nome,
            telefone: values.telefone,
            bairro: values.bairro,
            logradouro: values.logradouro,
            numero: values.numero,
            complemento: values.complemento,
            cep: values.cep,
            municipio: {
                estado: values.estado,
                id: values.cidade?.id,
                descricao: values.cidade?.descricao,
                codMunicipio: values.cidade?.codMunicipio
            },
            ativo: values.ativo
        };

        postNoResponse("/forum/save", body)
            .then(() => {
                successAlert(!id ? "Fórum criado com sucesso!" : "Alterações salvas!", handleSucess());
            })
            .catch(() => {
                errorAlert(!id ? "Erro ao criar fórum!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    function handleSucess() {
        closeCreateEdit()
    }

    return (
        <Card id="user-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Fórum</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    nome: '',
                    telefone: '',
                    estado: null,
                    cidade: null,
                    bairro: '',
                    logradouro: '',
                    numero: '',
                    complemento: '',
                    cep: '',
                    ativo: false
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Form id={configForm.formId} autoComplete="off">
                            <FetchAndSetData
                                id={id}
                                setIsLoadingInfo={setIsLoadingInfo}
                                closeCreateEdit={closeCreateEdit} />

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={nome.type}
                                            label={nome.label}
                                            name={nome.name}
                                            value={values.nome}
                                            placeholder={nome.placeholder}
                                            error={errors.nome?.length > 0 && touched.nome}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={telefone.type}
                                            label={telefone.label}
                                            name={telefone.name}
                                            value={values.telefone}
                                            placeholder={telefone.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Estado</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.descricao}
                                            name={estado.name}
                                            placeholder={estado.placeholder}
                                            value={values.estado}
                                            options={estadoList}
                                            onChange={selectedOptions => {
                                                setFieldValue('estado', selectedOptions);
                                                selectEstadoCidade(selectedOptions)
                                            }}
                                        />
                                        {errors.estado && touched.estado && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.estado}
                                            </MDTypography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cidade</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.descricao}
                                            name={cidade.name}
                                            placeholder={cidade.placeholder}
                                            value={values.cidade}
                                            options={cidadeList}
                                            onChange={selectedOptions => {
                                                setFieldValue('cidade', selectedOptions);
                                            }}
                                        />
                                        {errors.cidade && touched.cidade && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.cidade}
                                            </MDTypography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={logradouro.type}
                                            label={logradouro.label}
                                            name={logradouro.name}
                                            value={values.logradouro}
                                            placeholder={logradouro.placeholder}
                                            error={errors.logradouro?.length > 0 && touched.logradouro}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={bairro.type}
                                            label={bairro.label}
                                            name={bairro.name}
                                            value={values.bairro}
                                            placeholder={bairro.placeholder}
                                            error={errors.bairro?.length > 0 && touched.bairro}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={numero.type}
                                            label={numero.label}
                                            name={numero.name}
                                            value={values.numero}
                                            placeholder={numero.placeholder}
                                            error={errors.numero?.length > 0 && touched.numero}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={complemento.type}
                                            label={complemento.label}
                                            name={complemento.name}
                                            value={values.complemento}
                                            placeholder={complemento.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={cep.type}
                                            label={cep.label}
                                            name={cep.name}
                                            value={values.cep}
                                            placeholder={cep.placeholder}
                                            onKeyUp={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length === 8) {
                                                    buscarCep(e.target.value, setFieldValue);
                                                }
                                            }}
                                            error={errors.cep?.length > 0 && touched.cep}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={2} style={{ paddingTop: 40 }}>
                                        {isLoadingInfo ? <Skeleton width={"80%"} sx={{ mt: 2 }} /> :
                                            <Box display="flex" justifyContent="center" alignItems="center" lineHeight={1}>
                                                <MDTypography variant="button">
                                                    Ativo
                                                </MDTypography>
                                                <Box ml={1}>
                                                    <MDSwitch
                                                        value={values.ativo ?? false}
                                                        setFieldValue={setFieldValue}
                                                        campo={"ativo"} />
                                                </Box>
                                            </Box>
                                        }
                                    </Grid>
                                </Grid>
                            </MDBox>
                            <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                                <MDButton
                                    disabled={isLoadingInfo}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    type="submit"
                                    endIcon={isSubmitting ?
                                        <CircularProgress
                                            color="inherit"
                                            size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                    {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </Box>
                        </Form>)
                }}
            </Formik>
        </Card>
    );
};

ForumCreateEdit.propTypes = {
    id: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(ForumCreateEdit);

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/forum/by-id/${id}`).then(data => {
                const { id, nome, telefone, logradouro, bairro, cep, numero, complemento, municipio, ativo } = data;
                setFieldValue('nome', nome);
                setFieldValue('telefone', telefone);
                setFieldValue('logradouro', logradouro);
                setFieldValue('cep', cep);
                setFieldValue('numero', numero);
                setFieldValue('complemento', complemento);
                setFieldValue('estado', municipio?.estado);
                setFieldValue('cidade', municipio);
                setFieldValue('bairro', bairro);
                setFieldValue('id', id);
                setFieldValue('ativo', ativo);
                setIsLoadingInfo(false);
            }).catch((erro) => errorAlert("Fórum não encontrado!", closeCreateEdit));
        }
    }, [id]);

    return null;
};

export const configForm = {
    formId: "configuration-form",
    formField: {
        nome: {
            name: "nome",
            label: "Nome",
            placeholder: "Informe o nome...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        telefone: {
            name: "telefone",
            label: "Telefone",
            placeholder: "Informe o telefone...",
        },
        logradouro: {
            name: "logradouro",
            label: "Logradouro",
            placeholder: "Informe o logradouro...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",

        },
        bairro: {
            name: "bairro",
            label: "Bairro",
            placeholder: "Informe o bairro...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",

        },
        numero: {
            name: "numero",
            label: "Número",
            placeholder: "Informe o número...",
        },
        complemento: {
            name: "complemento",
            label: "Complemento",
            placeholder: "Informe o complemento...",
        },
        cep: {
            name: "cep",
            label: "CEP",
            placeholder: "Informe o CEP...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",

        },
        cidade: {
            name: "cidade",
            label: "Cidade",
            placeholder: "Selecione",
            errorMsg: "Campo Obrigatório.",
        },
        estado: {
            name: "estado",
            label: "Estado",
            placeholder: "Selecione",
            errorMsg: "Campo Obrigatório.",
        },
    }
}

const {
    formField: {
        nome,
        estado,
        cidade,
        logradouro,
        bairro,
        numero,
        cep
    },
} = configForm;

export const validationSchema = Yup.object().shape({
    [nome.name]: Yup.string()
        .required(nome.errorMsg),
    [logradouro.name]: Yup.string()
        .required(logradouro.errorMsg),
    [bairro.name]: Yup.string()
        .required(bairro.errorMsg),
    [cep.name]: Yup.string()
        .required(cep.errorMsg),
    [estado.name]: Yup.object()
        .required(estado.errorMsg),
    [cidade.name]: Yup.object()
        .required(cidade.errorMsg),
});
